import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { Plane, Loader2, CheckCircle2, Mail } from "lucide-react";
import { api, formatSlotTime } from "@/lib/api";
import { Navbar, Footer } from "@/pages/Home";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          clearInterval(timer);
          const b = await api.get(`/bookings/by-session/${sessionId}`);
          setBooking(b.data);
          setStatus("paid");
        } else if (attempts > 20) {
          clearInterval(timer);
          setStatus("timeout");
        }
      } catch {
        if (attempts > 20) {
          clearInterval(timer);
          setStatus("timeout");
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-32 pb-24 max-w-2xl mx-auto px-4">
        {status === "polling" && (
          <div data-testid="payment-polling" className="text-center py-24">
            <Loader2 className="w-10 h-10 animate-spin text-sky-600 mx-auto" />
            <h1 className="text-2xl font-bold mt-6 text-[#0A192F]">Confirming your payment...</h1>
            <p className="text-slate-500 mt-2 text-sm">Hold tight, we're printing your boarding pass.</p>
          </div>
        )}

        {status === "paid" && booking && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-center mb-10">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h1 data-testid="confirmation-heading" className="font-display uppercase text-4xl font-bold text-[#0A192F] mt-4">You're booked!</h1>
              <p className="text-slate-500 mt-2 flex items-center justify-center gap-2 text-sm">
                <Mail className="w-4 h-4" /> A confirmation email is on its way to {booking.email}
              </p>
            </div>

            {/* Boarding pass */}
            <div data-testid="boarding-pass" className="rounded-2xl overflow-hidden shadow-2xl">
              <div className="navy-panel text-white px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-[#F59E0B]" />
                  </span>
                  <span className="font-display font-bold text-lg uppercase tracking-wide">Cabin Crew Mentoring — Boarding Pass</span>
                </div>
                <span className="text-xs uppercase tracking-[0.25em] text-sky-400 font-semibold hidden sm:block">First Class Prep</span>
              </div>
              <div className="bg-white px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Passenger</div>
                  <div data-testid="bp-name" className="font-bold text-[#0A192F] mt-1">{booking.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Flight</div>
                  <div data-testid="bp-flight" className="font-bold text-[#0A192F] mt-1">CCM-{booking.session_key === "interview_ready" ? "101" : booking.session_key === "cabin_manager" ? "202" : "303"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">From</div>
                  <div className="font-bold text-[#0A192F] mt-1">YOU</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">To</div>
                  <div className="font-bold text-[#F59E0B] mt-1">HIRED</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Date</div>
                  <div data-testid="bp-date" className="font-bold text-[#0A192F] mt-1">{format(parseISO(booking.date), "d MMM yyyy")}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Boarding</div>
                  <div data-testid="bp-time" className="font-bold text-[#0A192F] mt-1">{formatSlotTime(booking.time)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Session</div>
                  <div className="font-bold text-[#0A192F] mt-1 text-xs leading-snug">{booking.session?.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Seat</div>
                  <div className="font-bold text-[#0A192F] mt-1">1A</div>
                </div>
              </div>
              <div className="boarding-perforation h-0.5 bg-transparent mx-6" />
              <div className="bg-white px-8 py-5 flex items-center justify-between">
                <div className="barcode h-10 w-48" />
                {booking.meeting_link ? (
                  <a
                    data-testid="bp-join-link"
                    href={booking.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full bg-[#0A192F] text-white text-xs font-bold px-5 py-2.5 hover:bg-sky-800 transition-colors duration-200"
                  >
                    Join Your Video Call
                  </a>
                ) : (
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold text-right">
                    Sydney time — video call link<br />sent by Tony before your session
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mt-10">
              <Link to="/" data-testid="back-home-btn" className="text-sky-700 font-semibold text-sm hover:underline">Back to homepage</Link>
            </div>
          </motion.div>
        )}

        {(status === "timeout" || status === "error") && (
          <div data-testid="payment-timeout" className="text-center py-24">
            <h1 className="text-2xl font-bold text-[#0A192F]">We're still confirming your payment</h1>
            <p className="text-slate-500 mt-3 text-sm max-w-md mx-auto">
              If you completed payment, your booking will be confirmed shortly and you'll receive an email. If you have any concerns, contact tony@cabincrewmentoring.com.
            </p>
            <Link to="/" data-testid="timeout-home-btn" className="inline-block mt-8 text-sky-700 font-semibold text-sm hover:underline">Back to homepage</Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
